import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import type { Brand, BrandId } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

const MAX_ROWS = 50;

/**
 * A searchable list of catalog brands to pick ONE from — the body of every "which brand?" dialog
 * (link a model, merge into, alias of). Filters on the same fold that keys `brand-aliases`, so a pasted
 * "Whirlpool Corporation" still hits WHIRLPOOL. Junk and merged brands never appear: nothing should be
 * pointed at a tombstone.
 */
export const BrandSearchList: FC<{
    brands: Brand[];
    /** Brands the pick must not land on (the brand being merged away, say), shown with a reason. */
    exclude?: Partial<Record<BrandId, string>>;
    /** The brand already chosen, badged so the admin can see it in the list. */
    currentId?: BrandId;
    initialQuery?: string;
    disabled?: boolean;
    emptyMessage?: ReactNode;
    onSelect: (brand: Brand) => void;
}> = ({ brands, exclude = {}, currentId, initialQuery = '', disabled, emptyMessage, onSelect }) => {
    const [query, setQuery] = useState(initialQuery);
    const needle = normalizeBrandName(query).key;
    const candidates = brands.filter((brand) => brand.reviewStatus !== 'junk' && !brand.mergedInto);
    const filtered = (
        needle
            ? candidates.filter(
                  (brand) =>
                      brand.canonicalKey.includes(needle) ||
                      brand.aliases.some((alias) => normalizeBrandName(alias).key.includes(needle)),
              )
            : candidates
    ).slice(0, MAX_ROWS);

    return (
        <div className="space-y-3">
            <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search brands…"
                autoFocus
                autoComplete="off"
                disabled={disabled}
            />
            <div className="max-h-64 overflow-y-auto rounded-md border">
                {filtered.length === 0 ? (
                    <div className="p-3 section-description">{emptyMessage ?? 'No brands match.'}</div>
                ) : (
                    <ul className="divide-y">
                        {filtered.map((brand) => {
                            const reason = exclude[brand.id];
                            return (
                                <li key={brand.id}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 disabled:opacity-50"
                                        onClick={() => onSelect(brand)}
                                        disabled={disabled || !!reason}
                                        title={reason}
                                    >
                                        <span className="break-all font-medium">
                                            {brand.displayName ?? brand.canonicalName}
                                            {brand.displayName && (
                                                <span className="ml-2 text-muted-foreground">
                                                    {brand.canonicalName}
                                                </span>
                                            )}
                                        </span>
                                        {reason && <Badge variant="outline">{reason}</Badge>}
                                        {brand.id === currentId && <Badge variant="outline">Linked</Badge>}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};
